import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";
import { Bar, Radar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const MUDAS = [
  "Transportation",
  "Inventory",
  "Motion",
  "Waiting",
  "Overproduction",
  "Overprocessing",
  "Defects"
];

const SCORE_LABELS = {
  0: "Never",
  1: "Rarely",
  2: "Sometimes",
  3: "Frequently",
  4: "Very Frequently"
};

const USER_TYPES = {
  Family: [
    "We move items repeatedly due to poor organization",
    "We store excess unused items",
    "We search daily for misplaced things",
    "We waste time waiting due to poor coordination",
    "We cook/buy more than needed",
    "We redo tasks due to lack of planning",
    "Avoidable mistakes cause stress or rework"
  ],
  Mother: [
    "Groceries are not planned weekly",
    "Meal planning is not done in advance",
    "Tasks are not delegated efficiently",
    "Forgotten school/home items cause repeat work",
    "Last-minute stress is frequent",
    "Household processes are not standardized",
    "Avoidable mistakes increase workload"
  ],
  Student: [
    "I switch between too many apps/books",
    "I collect too many unused notes/resources",
    "I waste time setting up study space",
    "I procrastinate and rush later",
    "I rewrite notes excessively",
    "I study topics not required",
    "Silly mistakes reduce my marks"
  ],
  MSME: [
    "Materials are moved unnecessarily",
    "Dead stock is high",
    "Workers walk excessively",
    "Machines/staff are idle frequently",
    "Production exceeds demand",
    "Documentation/approvals are excessive",
    "Rework percentage is high"
  ],
  Teacher: [
    "Paperwork is excessive",
    "Waiting for approvals is common",
    "Result errors occur",
    "Class time is lost in setup",
    "Lessons are not optimized",
    "Repetitive explanations needed",
    "Student engagement is low due to process gaps"
  ]
};

export default function MudasHome() {
const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [userType, setUserType] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const questions = USER_TYPES[userType] || [];
  const maxScore = questions.length * 4;

  const handleAnswer = (index, value) => {
    setAnswers((prev) => ({
      ...prev,
      [index]: Number(value)
    }));
  };

  const totalScore = useMemo(
    () => Object.values(answers).reduce((a, b) => a + b, 0),
    [answers]
  );

  const efficiency = useMemo(() => {
    if (!maxScore) return 0;
    return (((maxScore - totalScore) / maxScore) * 100).toFixed(0);
  }, [totalScore, maxScore]);

  const progress = questions.length
    ? (Object.keys(answers).length / questions.length) * 100
    : 0;

  const getInsight = () => {
    if (totalScore <= 8)
      return "✅ Lean & Efficient System. Maintain & continuously improve.";
    if (totalScore <= 14)
      return "⚡ Moderate Waste. Focus on 1–2 high impact improvements.";
    if (totalScore <= 21)
      return "⚠️ High Waste. Structured correction plan required.";
    return "🚨 Critical Waste Zone. Immediate system redesign needed.";
  };

  const mudaBreakdown = useMemo(() => {
    return MUDAS.map((_, i) => answers[i] || 0);
  }, [answers]);

  const barData = {
    labels: MUDAS,
    datasets: [
      {
        label: "Waste Score",
        data: mudaBreakdown,
        backgroundColor: "rgba(59,130,246,0.7)"
      }
    ]
  };

  const radarData = {
    labels: MUDAS,
    datasets: [
      {
        label: "Lean Waste Profile",
        data: mudaBreakdown,
        backgroundColor: "rgba(34,197,94,0.3)",
        borderColor: "rgba(34,197,94,1)",
        borderWidth: 2
      }
    ]
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("7 Mudas Self Assessment Report", 20, 20);

    doc.setFontSize(12);
    doc.text(`User Type: ${userType}`, 20, 35);
    doc.text(`Total Score: ${totalScore}`, 20, 45);
    doc.text(`Lean Efficiency: ${efficiency}%`, 20, 55);
    doc.text(`Insight: ${getInsight()}`, 20, 65);

    let y = 80;
    MUDAS.forEach((muda, i) => {
      doc.text(`${muda}: ${answers[i] || 0} / 4`, 20, y);
      y += 10;
    });

    doc.save("7_Mudas_Assessment_Report.pdf");
  };

  const reset = () => {
    setUserType("");
    setAnswers({});
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-black p-4 text-sm">
        <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
            <div className="flex flex-col">
                <div className="text-lg lg:text-xl font-bold text-yellow-200">7 Mudas</div>
                <div className="text-[11px] text-gray-200">Self Assessment</div>
            </div>
    
            <div className="hidden md:flex items-center gap-3">
                {isLoggedIn ? (
                <>
                    <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
                    <button
                    onClick={handleLogout}
                    className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                    title="Logout"
                    >
                    ⏻
                    </button>
                </>
                ) : (
                <>
                    <button
                    onClick={() => navigate("/login")}
                    className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
                    >
                    🔐 
                    </button>
                </>
                )}
            </div>
            <button
                onClick={() => setIsMobileMenuOpen((s) => !s)}
                className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
                aria-label="Toggle menu"
            >
                {isMobileMenuOpen ? "×" : "☰"}
            </button>
            </header>
    
            {isMobileMenuOpen && (
            <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
                {isLoggedIn ? (
                <div className="flex flex-col items-center gap-3">
                    <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
                    <div className="flex gap-3">
                        <button
                        onClick={handleLogout}
                        className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                        >
                        ⏻
                        </button>
                    </div>
                </div>
                ) : (
                <div className="flex justify-center gap-6">
                    <Link to="/login" className="text-teal-300">🔐</Link>
                </div>
                )}
            </div>
        )}
        <p className="mb-2 text-white mt-2 font-semibold">Select User Type:</p>
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.keys(USER_TYPES).map((type) => (
            <button
                key={type}
                onClick={() => setUserType(type)}
                className="w-full text-xs bg-blue-600 text-white py-2 rounded-xl"
            >
                {type}
            </button>
            ))}
        </div>

        {!submitted && (
          <>
            <h2 className="font-semibold text-white mb-2 mt-2">{userType} Checklist</h2>
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-white mt-1">
                {Object.keys(answers).length} / {questions.length} answered
              </p>
            </div>

            {questions.map((q, i) => (
              <div key={i} className="mb-3">
                <p className="mb-1 text-white font-medium">{q}</p>
                <select
                  className="h-12 w-full border p-2 rounded-lg"
                  onChange={(e) =>
                    handleAnswer(i, e.target.value)
                  }
                  defaultValue=""
                >
                  <option value="" disabled>Select</option>
                  {Object.entries(SCORE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            <button
              disabled={progress !== 100}
              onClick={() => setSubmitted(true)}
              className="w-24 bg-green-600 text-white py-2 rounded-xl mt-2 disabled:opacity-50"
            >
              Submit
            </button>
          </>
        )}

        {submitted && (
          <>
            <h2 className="text-center text-white font-bold text-lg">Total Score: {totalScore}</h2>
            <p className="text-center text-white">Lean Efficiency: {efficiency}%</p>
            <p className="text-center text-white font-semibold mt-2">{getInsight()}</p>

            <div className="mt-4">
              <h3 className="font-semibold text-white mb-2">Muda Breakdown</h3>
              {MUDAS.map((muda, i) => (
                <div key={i} className="flex justify-between text-xs mb-1">
                  <span>{muda}</span>
                  <span>{answers[i] || 0} / 4</span>
                </div>
              ))}
            </div>

            <div className="mt-5"><Bar data={barData} /></div>
            <div className="mt-5"><Radar data={radarData} /></div>

            <button
              onClick={generatePDF}
              className="w-48 bg-purple-600 text-white py-2 rounded-xl mt-4"
            >
              Download PDF Report
            </button>

            <button
              onClick={reset}
              className="w-32 bg-gray-700 text-white py-2 rounded-xl mt-2"
            >
              Reassess
            </button>
          </>
        )}
    </div>
  );
}
